import React, { useState, useEffect, useRef } from "react";
import { Form, Input, Button, Checkbox, message } from "antd";
import {
	UserOutlined,
	LockOutlined,
	CheckCircleOutlined,
	CloseCircleOutlined,
	InfoCircleOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";
import { AuthLayout } from "@/shared/layout";
import { sessionEndedMessage } from "@/shared/lib/sessionMessages";
import { AppButton, BackButton } from "@/shared/components";
import {
	CodeInputField,
	LOGIN_EMAIL_RULES,
	LOGIN_PASSWORD_RULES,
	LOGIN_VALIDATION_MESSAGES,
	VerificationButton,
} from "@/features/auth";
import { authenticationService, LoginRequest, OTPLoginRequest } from "@/api/authentication";
import { useAuth } from "@/shared/contexts/AuthContext";
import { AnimatePresence, motion } from "framer-motion";
import { useUserLog, LogAction, LogResultStatus } from "@/shared/lib/userLog";
import googleIconUrl from "@/assets/icons/google.png?url";
import { normalizeEmail } from "@/shared/utils/email";
import { pressedDarken, pressedFade } from "@/shared/theme/pressed";

// Styled Components for form-specific elements
const SuccessMessage = styled.div`
	background: #f6ffed;
	border: 1px solid #b7eb8f;
	border-radius: 8px;
	padding: 0.75rem 1rem;
	margin-bottom: 1.5rem;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	color: #52c41a;
	font-size: 0.875rem;
`;

const InfoMessage = styled.div`
	background: #e6f4ff;
	border: 1px solid #91caff;
	border-radius: 8px;
	padding: 0.75rem 1rem;
	margin-bottom: 1.5rem;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	color: #0958d9;
	font-size: 0.875rem;
`;

const ErrorMessage = styled.div`
	background: #fff2f0;
	border: 1px solid #ffccc7;
	border-radius: 8px;
	padding: 0.75rem 1rem;
	margin-bottom: 12px;
	display: flex;
	align-items: flex-start;
	gap: 0.5rem;
	color: #ff4d4f;
	font-size: 0.875rem;

	.error-icon {
		color: #ff4d4f;
		font-size: 1rem;
		margin-top: 0.125rem;
	}

	.error-content {
		flex: 1;
	}

	.error-title {
		font-weight: 700;
		color: #000;
		margin-bottom: 0.25rem;
	}

	.error-message {
		color: #000;
		font-size: 0.875rem;
	}
`;

const StyledForm = styled(Form)`
	.ant-form-item {
		margin-bottom: 1.5rem;
	}

	.ant-form-item:last-child {
		margin-bottom: 1rem;
	}

	.ant-form-item-label {
		padding-bottom: 0.5rem;
	}

	.ant-form-item-label > label {
		font-weight: 500;
		color: #333;
	}

	.ant-input-affix-wrapper {
		height: 47px;
		border-radius: 50px;
		border: 1px solid #d9d9d9;

		&:hover,
		&:focus {
			border-color: ${CUSTOM_COLORS.PRIMARY};
		}
	}

	.ant-input {
		font-size: 16px;
	}
`;

const AnimatedBlock = styled(motion.div)`
	width: 100%;
`;

const ForgotPasswordLink = styled(Link)`
	color: #666;
	font-size: 0.875rem;
	text-decoration: none;

	&:hover {
		color: ${CUSTOM_COLORS.PRIMARY};
	}

	${pressedFade}
`;

const LoginButton = styled(Button)`
	height: 36px;
	border-radius: 20px;
	font-size: 0.9rem;
	font-weight: 500;
	background: ${CUSTOM_COLORS.PRIMARY};
	border: none;
	width: 100%;

	&:hover {
		background: ${CUSTOM_COLORS.PRIMARY} !important;
		opacity: 0.9;
	}

	&:disabled {
		background: #ebebe4 !important;
		opacity: 0.9;
	}

	${pressedDarken}
`;

const GoogleButton = styled(Button)`
	height: 36px;
	border-radius: 20px;
	font-size: 0.9rem;
	font-weight: 500;
	border: 1px solid #d9d9d9;
	background: white;
	color: #333;
	width: 100%;
	margin-top: 12px;

	&:hover {
		border-color: ${CUSTOM_COLORS.PRIMARY};
		color: ${CUSTOM_COLORS.PRIMARY};
	}

	${pressedDarken}
`;

const SubmitFormItem = styled(Form.Item)`
	margin-bottom: 0 !important;
`;

const OTCButton = styled(Button)`
	color: #ffffff;
	height: 36px;
	border-radius: 20px;
	font-size: 0.9rem;
	font-weight: 500;
	background: ${CUSTOM_COLORS.SECONDARY};
	border: none;
	width: 100%;

	&:hover {
		background: ${CUSTOM_COLORS.SECONDARY} !important;
		opacity: 0.9;
		color: #ffffff !important;
	}

	${pressedDarken}
`;

const OtpLoginButton = styled(AppButton)`
	width: 100%;
	font-size: 14px;

	${pressedDarken}
`;

const DividerText = styled.div`
	text-align: center;
	margin: 1.25rem 0;
	color: #999;
	font-size: 0.8rem;
	position: relative;

	&::before {
		content: "";
		position: absolute;
		top: 50%;
		left: 0;
		right: 0;
		height: 1px;
		background: #e8e8e8;
		z-index: 1;
	}

	span {
		background: white;
		padding: 0 1rem;
		position: relative;
		z-index: 2;
	}
`;

const OtpBackAction = styled.div`
	position: absolute;
	top: 15px;
	left: 20px;
	z-index: 6;

	@media (max-width: 640px) {
		top: 16px;
		left: 16px;
	}
`;

const OtpSendAction = styled.div`
	width: 100%;
`;

const OtpSendButton = styled(AppButton)`
	width: 100%;
	height: 47px;
	font-size: 14px;

	${pressedDarken}
`;

const OtpEmailText = styled.strong`
	font-weight: 600;
	color: ${CUSTOM_COLORS.PRIMARY};
`;

const Login: React.FC = () => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [loginMode, setLoginMode] = useState<"regular" | "otp">("regular");
	const [showCodeVerification, setShowCodeVerification] = useState(false);
	const [verificationCode, setVerificationCode] = useState("");
	const [countdown, setCountdown] = useState(0);
	const [userEmail, setUserEmail] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [credentialsError, setCredentialsError] = useState("");
	const [showSuccess, setShowSuccess] = useState(false);
	const [resendCount, setResendCount] = useState(0);
	const [isOtpSuccess, setIsOtpSuccess] = useState(false);
	const lastAutoSubmittedCodeRef = useRef<string | null>(null);
	const otpSuccessTimeoutRef = useRef<number | null>(null);
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const returnTo = searchParams.get("returnTo") || "/campaigns";
	// Set by AuthContext when it sent the user here because the session ended.
	const sessionNotice = sessionEndedMessage(searchParams.toString() ? `?${searchParams.toString()}` : "");
	const { login } = useAuth();
	const { logEvent } = useUserLog();
	const watchedEmail = Form.useWatch("email", form);
	const hasEmail = !!watchedEmail?.trim();

	useEffect(() => {
		if (countdown > 0) {
			const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
			return () => clearTimeout(timer);
		}
	}, [countdown]);

	useEffect(() => {
		if (loginMode !== "otp" || !showCodeVerification || loading) {
			return;
		}

		if (verificationCode.length === 6 && lastAutoSubmittedCodeRef.current !== verificationCode) {
			lastAutoSubmittedCodeRef.current = verificationCode;
			form.submit();
		}
	}, [verificationCode, loginMode, showCodeVerification, loading, form]);

	const handleSendCode = async () => {
		let email = userEmail;
		if (!showCodeVerification) {
			try {
				const values = await form.validateFields(["email"]);
				email = normalizeEmail(values.email);
			} catch {
				return;
			}
		}

		setLoading(true);
		setErrorMessage(""); // Clear any previous errors
		try {
			// Call API to generate OTP
			await authenticationService.generateLoginOTP({ email });
			setUserEmail(email);
			setVerificationCode("");
			setResendCount((prev) => prev + 1);
			lastAutoSubmittedCodeRef.current = null;
			message.success("Verification code sent to your email!");
			setCountdown(60); // 60 seconds cooldown
			setShowCodeVerification(true);
		} catch (error: any) {
			const errorMsg = error.response?.data?.message || "Internal Server Error";
			setErrorMessage(errorMsg);
			setVerificationCode("");
			setResendCount((prev) => prev + 1);
		} finally {
			setLoading(false);
		}
	};

	const handleCodeComplete = (code: string) => {
		setVerificationCode(code);
	};

	const handleCodeChange = (code: string) => {
		setVerificationCode(code);
		if (code.length < 6) {
			lastAutoSubmittedCodeRef.current = null;
		}
		if (code.length > 0) setErrorMessage("");
	};

	const onFinish = async (values: any) => {
		setErrorMessage(""); // Clear any previous errors
		setCredentialsError("");
		setShowSuccess(false);

		if (loginMode === "regular") {
			// Regular login flow: email + password
			setLoading(true);
			logEvent({
				action: LogAction.USER_LOGIN_ATTEMPT,
				properties: { login_method: "password" },
			});
			try {
				const loginData: LoginRequest = {
					email: normalizeEmail(values.email),
					password: values.password,
					rememberMe: Boolean(values.remember),
				};

				const response = await authenticationService.login(loginData);

				// Update authentication context
				login(response.user);

				logEvent({
					action: LogAction.USER_LOGIN_SUCCESS,
					result_status: LogResultStatus.SUCCESS,
					properties: { login_method: "password" },
				});

				setShowSuccess(true);
				message.success(`Welcome back, ${response.user.name}!`);
				navigate(returnTo);
			} catch (error: any) {
				const errorMsg = LOGIN_VALIDATION_MESSAGES.invalidCredentials;
				setCredentialsError(errorMsg);
				logEvent({
					action: LogAction.USER_LOGIN_FAILED,
					result_status: LogResultStatus.FAIL,
					error_code: error?.response?.status ? String(error.response.status) : "network",
					properties: { login_method: "password", reason: errorMsg },
				});
			} finally {
				setLoading(false);
			}
		} else if (loginMode === "otp") {
			if (!verificationCode) {
				setErrorMessage("Please enter the 6-digit code");
				return;
			}

			setLoading(true);
			logEvent({
				action: LogAction.USER_LOGIN_ATTEMPT,
				properties: { login_method: "otp" },
			});
			try {
				const otpLoginData: OTPLoginRequest = {
					email: normalizeEmail(userEmail || values.email),
					otp: verificationCode,
				};

				const response = await authenticationService.loginWithOTP(otpLoginData);

				// Update authentication context
				login(response.user);

				logEvent({
					action: LogAction.USER_LOGIN_SUCCESS,
					result_status: LogResultStatus.SUCCESS,
					properties: { login_method: "otp" },
				});

				setIsOtpSuccess(true);

				clearOtpTimeout();
				otpSuccessTimeoutRef.current = window.setTimeout(() => {
					setShowSuccess(true);
					message.success(`Welcome back, ${response.user.name}!`);
					navigate(returnTo);
				}, 800);
			} catch (error: any) {
				const errorMsg = "Invalid code";
				setErrorMessage(errorMsg);
				setVerificationCode("");
				setIsOtpSuccess(false); // Make sure it's reset on error
				setResendCount((prev) => prev + 1);
				lastAutoSubmittedCodeRef.current = null;
				logEvent({
					action: LogAction.USER_LOGIN_FAILED,
					result_status: LogResultStatus.FAIL,
					error_code: error?.response?.status ? String(error.response.status) : "network",
					properties: { login_method: "otp", reason: errorMsg },
				});
			} finally {
				setLoading(false);
			}
		}
	};

	const handleGoogleLogin = () => {
		logEvent({
			action: LogAction.USER_LOGIN_ATTEMPT,
			properties: { login_method: "google" },
		});
		const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
		const redirectUri = `${window.location.origin}/auth/callback`;
		const url = new URL("https://accounts.google.com/o/oauth2/v2/auth");
		url.searchParams.set("client_id", clientId);
		url.searchParams.set("redirect_uri", redirectUri);
		url.searchParams.set("response_type", "code");
		url.searchParams.set("scope", "openid email profile");
		url.searchParams.set("prompt", "select_account");
		// Preserve the post-login destination (e.g. a creator invitation link) across
		// the Google OAuth round trip so the callback can return the user there.
		url.searchParams.set("state", returnTo);
		window.location.href = url.toString();
	};

	const clearOtpTimeout = () => {
		if (otpSuccessTimeoutRef.current) {
			clearTimeout(otpSuccessTimeoutRef.current);
			otpSuccessTimeoutRef.current = null;
		}
	};

	const handleOTPLogin = () => {
		clearOtpTimeout();
		setLoginMode("otp");
		setShowCodeVerification(false);
		setVerificationCode("");
		lastAutoSubmittedCodeRef.current = null;
		setErrorMessage(""); // Clear any previous errors
		setCredentialsError("");
		form.resetFields();
	};

	const handleRegularLogin = () => {
		clearOtpTimeout();
		setLoginMode("regular");
		setShowCodeVerification(false);
		setVerificationCode("");
		lastAutoSubmittedCodeRef.current = null;
		setErrorMessage(""); // Clear any previous errors
		setCredentialsError("");
		form.resetFields();
	};

	useEffect(() => {
		return () => {
			// Clear any pending OTP-success timeout when component unmounts
			clearOtpTimeout();
		};
	}, []);

	return (
		<AuthLayout
			title={loginMode === "otp" && "Get a 6-digit code"}
			subtitle={
				loginMode === "otp" ? (
					showCodeVerification && userEmail ? (
						<>
							We sent a temporary sign in code to <OtpEmailText>{userEmail}</OtpEmailText>
						</>
					) : (
						"Enter your email to receive a temporary sign in code"
					)
				) : (
					"Log in to the Creatorverse"
				)
			}
			backLinkTo={searchParams.get("returnTo") || undefined}
		>
			<div style={{ marginTop: 16 }}>
				{loginMode === "otp" && (
					<OtpBackAction>
						<BackButton label="Back" onClick={handleRegularLogin} />
					</OtpBackAction>
				)}
				{sessionNotice && !showSuccess && (
					<InfoMessage role="status">
						<InfoCircleOutlined />
						<span>{sessionNotice}</span>
					</InfoMessage>
				)}
				<AnimatePresence mode="wait" initial={false}>
					{showSuccess && (
						<AnimatedBlock
							key="login-success"
							initial={{ opacity: 0, y: -6 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -6 }}
							transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
						>
							<SuccessMessage>
								<CheckCircleOutlined />
								<span>Successful</span>
								<span>•</span>
								<span>You are now logging in</span>
							</SuccessMessage>
						</AnimatedBlock>
					)}

					{loginMode === "otp" && !showCodeVerification && !!errorMessage && (
						<AnimatedBlock
							key={`login-error-${errorMessage}`}
							initial={{ opacity: 0, y: -6 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -6 }}
							transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
						>
							<ErrorMessage>
								<CloseCircleOutlined className="error-icon" />
								<div className="error-content">
									<div className="error-title">Error</div>
									<div className="error-message">{errorMessage}</div>
								</div>
							</ErrorMessage>
						</AnimatedBlock>
					)}
				</AnimatePresence>

				<AnimatedBlock
					key={`${loginMode}-${showCodeVerification ? "code" : "form"}`}
					initial={{ opacity: 0, x: 14 }}
					animate={{ opacity: 1, x: 0 }}
					exit={{ opacity: 0, x: -10 }}
					transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
				>
					<StyledForm
						form={form}
						name="login"
						onFinish={onFinish}
						onValuesChange={() => {
							if (credentialsError) setCredentialsError("");
						}}
						layout="vertical"
						initialValues={{
							remember: true,
						}}
					>
						{!showCodeVerification && (
							<Form.Item name="email" label="Email" rules={LOGIN_EMAIL_RULES}>
								<Input prefix={<UserOutlined />} placeholder="Enter here" size="large" />
							</Form.Item>
						)}

						{loginMode === "regular" && (
							<Form.Item
								name="password"
								label="Password"
								rules={LOGIN_PASSWORD_RULES}
								validateStatus={credentialsError ? "error" : undefined}
								help={credentialsError || undefined}
							>
								<Input.Password prefix={<LockOutlined />} placeholder="Password" size="large" />
							</Form.Item>
						)}

						{loginMode === "otp" && !showCodeVerification && (
							<Form.Item style={{ marginBottom: 0 }}>
								<OtpSendAction>
									<OtpSendButton
										type="button"
										variant="primary"
										onClick={handleSendCode}
										disabled={!hasEmail || loading}
									>
										Send code
									</OtpSendButton>
								</OtpSendAction>
							</Form.Item>
						)}

						{loginMode === "otp" && showCodeVerification && (
							<>
								<CodeInputField
									error={errorMessage}
									resetSignal={resendCount}
									onComplete={handleCodeComplete}
									onChange={handleCodeChange}
									disabled={loading}
									isSuccess={isOtpSuccess}
								/>
								<Form.Item style={{ marginBottom: 0 }}>
									<VerificationButton
										type="button"
										idleVariant="outline"
										verifying={(loading && verificationCode.length === 6) || isOtpSuccess}
										onClick={() => void handleSendCode()}
										disabled={loading || isOtpSuccess || countdown > 0}
									>
										{countdown > 0 ? `Resend in ${countdown}s` : "Resend code"}
									</VerificationButton>
								</Form.Item>
							</>
						)}

						{loginMode === "regular" && (
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									marginBottom: "1.5rem",
								}}
							>
								<Form.Item name="remember" valuePropName="checked" noStyle>
									<Checkbox>Remember me</Checkbox>
								</Form.Item>
								<ForgotPasswordLink to="/forgot-password">Forgot password?</ForgotPasswordLink>
							</div>
						)}

						{loginMode === "regular" && (
							<SubmitFormItem>
								<LoginButton type="primary" htmlType="submit" loading={loading}>
									Log in
								</LoginButton>
							</SubmitFormItem>
						)}
					</StyledForm>
				</AnimatedBlock>

				{loginMode === "regular" && (
					<AnimatePresence mode="wait" initial={false}>
						{!showCodeVerification && (
							<AnimatedBlock
								key="login-alt-actions"
								initial={{ opacity: 0, y: 6 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: 6 }}
								transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
							>
								<GoogleButton
									icon={
										<img
											src={googleIconUrl}
											alt=""
											style={{ width: 32, height: 32, display: "block" }}
										/>
									}
									onClick={handleGoogleLogin}
								>
									Log in with Google
								</GoogleButton>

								<DividerText>
									<span>OR</span>
								</DividerText>

								{loginMode === "regular" ? (
									<OtpLoginButton variant="outline" onClick={handleOTPLogin}>
										Continue with OTP
									</OtpLoginButton>
								) : (
									<OTCButton icon={<UserOutlined />} onClick={handleRegularLogin}>
										Continue with Password
									</OTCButton>
								)}

								<div style={{ textAlign: "center", marginTop: 16, fontSize: 14, color: "#666" }}>
									Don't have an account?{" "}
									<Link to="/register" style={{ color: "var(--antd-primary)" }}>
										Sign up
									</Link>
								</div>
							</AnimatedBlock>
						)}
					</AnimatePresence>
				)}
				{showCodeVerification && <div style={{ height: 24 }} />}
			</div>
		</AuthLayout>
	);
};

export default Login;
